<template>
  <section
    class="text-reveal-module-component"
    :class="{
      'with-quotes': data.use_quotes_version,
    }"
    :style="{
      '--revmed-mini-text-color': data.color_settings.mini_heading_text_color,
      '--revmed-text-color': data.color_settings.text_color,
      '--revmed-bg-color': data.color_settings.bg_color,
      '--revmed-highlight-color': data.color_settings.highlight_color,
    }">
    <div
      :class="{
        'lg:px-0': !data.horizontal_padding,
        'inner-container': !data.use_quotes_version,
        'rr-container': data.use_quotes_version,
      }">
      <template v-if="data.use_quotes_version">
        <svg
          class="quote-svg left"
          xmlns="http://www.w3.org/2000/svg"
          width="98"
          height="98"
          viewBox="0 0 98 98"
          fill="none">
          <rect
            x="98"
            y="50.0576"
            width="47.9424"
            height="98"
            transform="rotate(90 98 50.0576)"
            fill="#13D377" />
          <rect
            x="2.38262e-05"
            width="47.9424"
            height="98"
            fill="#13D377" />
        </svg>
      </template>

      <h2
        v-if="data.mini_heading"
        class="mini-title">
        {{ data.mini_heading }}
      </h2>
      <div
        :id="`text-${uniqueId}`"
        ref="textToBreak"
        class="text"
        v-html="addBGDiv(data.text)" />

      <template v-if="data.use_quotes_version">
        <svg
          class="quote-svg right"
          xmlns="http://www.w3.org/2000/svg"
          width="98"
          height="98"
          viewBox="0 0 98 98"
          fill="none">
          <rect
            x="4.28372e-06"
            y="47.9425"
            width="47.9424"
            height="98"
            transform="rotate(-90 4.28372e-06 47.9425)"
            fill="#13D377" />
          <rect
            x="98"
            y="98.0001"
            width="47.9424"
            height="98"
            transform="rotate(180 98 98.0001)"
            fill="#13D377" />
        </svg>
      </template>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "TextRevealComponent",
    "CLASSNAME": "text-reveal-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import SplitType from 'split-type'
import { onMounted, onBeforeUnmount, ref } from 'vue'
import useUtils from '@/components/composables/useUtils'

const { genUUID } = useUtils()

gsap.registerPlugin(ScrollTrigger)

defineProps(['data'])
const splitted = ref()
const uniqueId = ref()
const textToBreak = ref()

const animateText = () => {
  gsap.fromTo(
    `#text-${uniqueId.value} div.line > *`,
    {
      y: '100%',
    },
    {
      y: '0%',
      duration: 1,
      stagger: 0.1,
      ease: 'power4.out',
      scrollTrigger: {
        id: `text-${uniqueId.value}`,
        trigger: `#text-${uniqueId.value}`,
        start: 'top-=80% bottom-=20%',
        end: 'bottom-=30% center',
        scrub: 1,
        markers: false,
      },
    }
  )
  gsap.fromTo(
    `#text-${uniqueId.value} div.highlight`,
    {
      width: '0%',
    },
    {
      width: '103%',
      duration: 1,
      stagger: 0.1,
      ease: 'power4.out',
      scrollTrigger: {
        id: `highlight-${uniqueId.value}`,
        trigger: `#text-${uniqueId.value}`,
        start: 'top+=10% top+=50%',
        end: 'bottom center',
        scrub: 1,
        markers: false,
      },
    }
  )
}

const addBGDiv = (html) => {
  return html.replace(
    /<em>/g,
    `<em><div class="highlight highlight-${uniqueId.value}"></div>`
  )
}

const splitIt = () => {
  const breakIt = document.querySelector(`#text-${uniqueId.value}`)

  if (!breakIt) {
    setTimeout(() => {
      splitIt()
    }, 100)
  } else {
    const textLines = new SplitType(`#text-${uniqueId.value} > *`, {
      types: 'lines, words',
    })
    splitted.value = textLines.lines
    animateText()
  }
}

onMounted(() => {
  uniqueId.value = genUUID()
  splitIt()
})

onBeforeUnmount(() => {
  const textAnim = ScrollTrigger.getById(`text-${uniqueId.value}`)
  if (textAnim) textAnim.kill()
  const highlightAnim = ScrollTrigger.getById(`highlight-${uniqueId.value}`)
  if (highlightAnim) highlightAnim.kill()
})
</script>

<style lang="scss" scoped>
.text-reveal-module-component {
  @apply module-container py-12;
  background-color: var(--revmed-bg-color);

  &.with-quotes {
    .rr-container {
      @apply lg:px-28;

      .text:deep() {
        @apply text-[30px] lg:text-[80px] leading-none tracking-normal lg:tracking-[-4px];
        * {
          @apply text-[32px] lg:text-[80px] leading-none tracking-normal lg:tracking-[-4px];
        }
      }
      .quote-svg {
        @apply absolute w-[41px] h-[41px] z-[-1] lg:w-[98px] lg:h-[98px];
        &.left {
          @apply bottom-[-25px] left-[25px] lg:bottom-[-98px] lg:left-[54px];
        }
        &.right {
          @apply top-[-41px] right-[25px] lg:top-[-98px] lg:right-[54px];
        }
      }
    }
  }

  .inner-container,
  .rr-container {
    .mini-title {
      @apply text-center mb-4 text-[16px] lg:text-[22px];
      color: var(--revmed-mini-text-color);
    }
    .text:deep() {
      @apply text-[30px] lg:text-[80px] leading-none tracking-normal lg:tracking-[-4px];
      * {
        @apply text-[26px] lg:text-[80px] leading-none tracking-normal lg:tracking-[-4px];
      }
      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply text-[var(--revmed-text-color)];
      }
      .line {
        @apply overflow-hidden;
      }
      .word {
        @apply text-[var(--revmed-text-color)];
      }
      em {
        @apply not-italic;
        .highlight {
          @apply h-full w-full absolute left-0 bottom-1 z-[-1]
          lg:bottom-[13px] lg:h-[55px];
          background-color: var(--revmed-highlight-color);
        }
      }
    }
  }
}
</style>
